import { IconNames } from '@icon/assets';

/**
 * A quick-access tile in the card at the top of the سایر page — the features
 * that are not in the bottom navigation but are reached often enough to earn a
 * tile rather than a list row.
 */
export interface MenuTile {
	title: string;
	icon: IconNames;
	/** The glyph's own accent — each feature is colored differently in the handoff. */
	iconClass: string;
	route?: string;
	url?: string;
	/** Where `url` goes on desktop instead, opened in a new tab. */
	desktopUrl?: string;
	dataCy: string;
}

/**
 * The tiles in handoff order. The card lays them out right-to-left, four to a
 * row, so this is also the order they read in.
 *
 * Each glyph carries its feature's accent color. Three of them are palette
 * tokens and follow the theme; ایزی چارت (#77c50f) and گردش حساب (#ce76aa) are
 * the products' own colors and have no token to name them, so they are written
 * literally and stay put in either theme, the way a logo's color does.
 */
export const MENU_TILES: readonly MenuTile[] = [
	{
		title: 'تاریخچه سفارش‌ها',
		icon: 'time',
		iconClass: 'text-red-500',
		route: '/orders/history',
		dataCy: 'menu-tile-order-history'
	},
	{
		title: 'تحویل فیزیکی',
		icon: 'physical-delivery',
		iconClass: 'text-mofid-500',
		route: '/physical-delivery',
		dataCy: 'menu-tile-physical-delivery'
	},
	{
		title: 'گردش حساب',
		icon: 'history',
		iconClass: 'text-[#ce76aa]',
		route: '/transactions',
		dataCy: 'menu-tile-account-turnover'
	},
	{
		title: 'ایزی چارت',
		icon: 'easychart',
		iconClass: 'text-[#77c50f]',
		route: '/easy-chart',
		dataCy: 'menu-tile-easy-chart'
	},
	{
		title: 'پیام‌ها',
		icon: 'comment-load',
		iconClass: 'text-green-500',
		route: '/messages',
		dataCy: 'menu-tile-messages'
	},
	{
		title: 'ایزی تریدر',
		icon: 'easy-fill',
		iconClass: 'text-gray-900',
		url: 'https://m.easytrader.ir',
		desktopUrl: 'https://d.easytrader.ir/',
		dataCy: 'menu-tile-easytrader'
	}
] as const;

/**
 * What a «عمومی» row does when it is tapped.
 *
 * `none` is a row the design calls for but whose behavior belongs to a later
 * story — it renders like the rest and says so to assistive tech rather than
 * looking tappable and doing nothing.
 */
export type MenuListAction = 'navigate' | 'social' | 'support' | 'none';

/** A row in the «عمومی» list. */
export interface MenuListItem {
	title: string;
	icon: IconNames;
	action: MenuListAction;
	/** Set only when `action` is `navigate`. */
	route?: string;
	dataCy: string;
}

export const MENU_LIST_ITEMS: readonly MenuListItem[] = [
	{ title: 'تنظیمات', icon: 'setting', action: 'navigate', route: '/settings', dataCy: 'menu-list-settings' },
	{ title: 'ثبت بازخورد', icon: 'star-edit', action: 'navigate', dataCy: 'menu-list-feedback' },
	{ title: 'ارتباط با پشتیبانی', icon: 'support', action: 'support', dataCy: 'menu-list-support' },
	{ title: 'شبکه‌های اجتماعی', icon: 'telegram', action: 'social', dataCy: 'menu-list-social' }
] as const;

/** Where the profile card's «حساب کاربری» link goes. */
export const MENU_ACCOUNT_ROUTE = '/account';

/** Every in-app page reached from سایر — the footer keeps سایر lit on all of them. */
export const MENU_ROUTES: readonly string[] = [
	MENU_ACCOUNT_ROUTE,
	...[...MENU_TILES, ...MENU_LIST_ITEMS].flatMap(item => (item.route ? [item.route] : []))
];

/** One of the channels offered by the شبکه‌های اجتماعی sheet. */
export interface SocialLink {
	title: string;
	icon: IconNames;
	url: string;
	dataCy: string;
}

/**
 * The published EasyTrader channels, same URLs the mobile app links to.
 * They open outside the app, so they are absolute and carry their own origin.
 */
export const SOCIAL_LINKS: readonly SocialLink[] = [
	{ title: 'تلگرام', icon: 'telegram', url: 'https://t.me/easytrader', dataCy: 'menu-social-telegram' },
	{
		title: 'اینستاگرام',
		icon: 'instagram',
		url: 'https://instagram.com/mofidsecurities?igshid=hsp4v1goqu00',
		dataCy: 'menu-social-instagram'
	}
] as const;

/**
 * The support line «تماس با 8700» dials, as a `tel:` URL: the phone opens its
 * dialer with the number already in, so the trader only has to press call.
 */
export const SUPPORT_PHONE_URL = 'tel:0218700';
